import api from "./api";
import axios from "axios";
import { DestinationDto } from "../interfaces/Dtos";
import { TaxIdentificationTypeInterface } from "../interfaces/TaxIdentificationTypeInterface";
import {
  Nullable,
  AccountInterface,
  ResponseInterface,
  DestinationInterface,
  WhiteLocationInterface,
  AccountRequestInterface,
  BUSellerConfig,
  RecurrentDestinationInterface,
} from "../interfaces";
import { sanitizeFulltextQuery } from "../utils/searchText";
import { AccountBlocked } from "../components/Account/AccountBlocked";
import {
  AccountSearchHint,
  AccountSearchRequest,
  AccountSearchResponse,
  AccountSearchStatus,
} from "../interfaces/AccountSearchInterface";
import {
  legacyQueryText,
  NormalizedSearch,
  DEFAULT_MIN_TOKEN_LENGTH,
} from "../utils/searchText";
import { clearAccountSearchCache } from "../utils/accountSearchCache";
import { AccountSiteBUDTO } from "../interfaces/Dtos/AccountSiteBUDTO";
import { AccountSiteLockDTO } from "../interfaces/Dtos/AccountSiteLockDTO";

const baseURLAPI = process.env.REACT_APP_API_HOST;

export const getAccount = async (
  accountId: string
): Promise<Nullable<AccountInterface>> => {
  if (!accountId) {
    return null;
  }
  const uri = `${baseURLAPI}/Account/GetAccount?accountId=${accountId}`;

  try {
    const response = await api.get(uri);

    if (response.status === 200) {
      return response.data;
    }
  } catch (error) {}

  return null;
};

export const getAccountByIdentification = async (
  abreviationName: string,
  identificationNumber: string
): Promise<ResponseInterface<AccountInterface>> => {
  if (!abreviationName || !identificationNumber) {
    return {
      model: null,
      didError: true,
      message: "Invalid parameters",
      errorMessage: "Invalid parameters",
    };
  }
  const uri = `${baseURLAPI}/GetAccount?abreviationName=${abreviationName}&identificationNumber=${identificationNumber}`;

  try {
    const response = await api.get(uri);

    return response.data;
  } catch (error) {
    const response = (error as any)?.response;

    if (response) {
      return response.data;
    }

    return {
      model: null,
      didError: true,
      message: `Error: ${error}`,
      errorMessage: "Application error 1",
    };
  }
};

export const saveAccount = async (
  account: AccountRequestInterface
): Promise<ResponseInterface<AccountInterface>> => {
  account.creationDate = new Date().toISOString();
  const body = JSON.stringify(account);

  const CancelToken = axios.CancelToken;
  const source = CancelToken.source();
  const uri = `${baseURLAPI}/Account`;

  try {
    const response = await api.post(uri, body, {
      headers: {
        "Content-Type": "application/json",
      },
      cancelToken: source.token,
    });

    if (response.status === 200) {
      // Cualquier resultado guardado puede haber quedado viejo.
      clearAccountSearchCache();
      return response.data;
    }
    return {
      model: null,
      didError: true,
      message: `Server status: ${response.status}`,
      errorMessage: "Server error",
    };
  } catch (error) {
    return {
      model: null,
      didError: true,
      message: `Error: ${error}`,
      errorMessage: "Application error 2",
    };
  }
};

export const updateAccount = async (
  account: AccountRequestInterface
): Promise<ResponseInterface<AccountInterface>> => {
  const body = JSON.stringify(account);

  const CancelToken = axios.CancelToken;
  const source = CancelToken.source();
  const uri = `${baseURLAPI}/Account`;

  try {
    const response = await api.put(uri, body, {
      headers: {
        "Content-Type": "application/json",
      },
      cancelToken: source.token,
    });

    if (response.status === 200) {
      // Cualquier resultado guardado puede haber quedado viejo.
      clearAccountSearchCache();
      return response.data;
    }
    return {
      model: null,
      didError: true,
      message: `Server status: ${response.status}`,
      errorMessage: "Server error",
    };
  } catch (error) {
    return {
      model: null,
      didError: true,
      message: `Error: ${error}`,
      errorMessage: "Application error 3",
    };
  }
};

/* ------------------------------------------------------------------ *
 * Búsqueda de clientes
 *
 * Dos caminos: `POST /v2/Account/Search` cuando ApiCore ya lo tiene, y el
 * `GET /Account?idStr=` de siempre cuando no. El cliente no decide por
 * configuración de compilación: sondea una vez por sesión y recuerda un 404
 * durante cinco minutos.
 * ------------------------------------------------------------------ */

/**
 * Igual que la libreta de destinatarios: el grupo Clientes enciende su
 * spinner en la pulsación, y una petición sin cota lo deja girando para
 * siempre. Viene de master (release 2026-09-16), que lo puso sobre la
 * versión anterior de `getFilteredAccounts`; aquí se conserva sobre la
 * reescritura.
 */
const FILTERED_ACCOUNTS_TIMEOUT = 15000;

/** Cuánto se recuerda que v2 no existe antes de volver a sondear. */
export const ACCOUNT_SEARCH_V2_RETRY_MS = 5 * 60 * 1000;

const V2_UNAVAILABLE_KEY = "accountSearchV2UnavailableAt";
const MIN_TOKEN_LENGTH_KEY = "accountSearchMinTokenLength";

const readSession = (key: string): string | null => {
  try {
    return sessionStorage.getItem(key);
  } catch (error) {
    return null;
  }
};

const writeSession = (key: string, value: string): void => {
  try {
    sessionStorage.setItem(key, value);
  } catch (error) {
    // Navegación privada: seguimos sin memoria, no es motivo para fallar.
  }
};

export const markAccountSearchV2Unavailable = (): void => {
  writeSession(V2_UNAVAILABLE_KEY, String(Date.now()));
};

export const clearAccountSearchV2Unavailable = (): void => {
  try {
    sessionStorage.removeItem(V2_UNAVAILABLE_KEY);
  } catch (error) {
    // Igual que arriba.
  }
};

export const isAccountSearchV2Unavailable = (): boolean => {
  const stored = readSession(V2_UNAVAILABLE_KEY);
  if (!stored) {
    return false;
  }

  const markedAt = Number(stored);
  if (!markedAt || Date.now() - markedAt >= ACCOUNT_SEARCH_V2_RETRY_MS) {
    clearAccountSearchV2Unavailable();
    return false;
  }

  return true;
};

/**
 * El mínimo de caracteres por token lo manda el API en cada respuesta v2.
 * Nunca se fija en el cliente: la regla de dos caracteres anclados se enciende
 * del lado del servidor y el frontend se entera por aquí.
 */
export const rememberMinTokenLength = (minTokenLength?: number): void => {
  if (!minTokenLength || minTokenLength < 1) {
    return;
  }
  writeSession(MIN_TOKEN_LENGTH_KEY, String(minTokenLength));
};

export const getMinTokenLength = (): number => {
  const stored = Number(readSession(MIN_TOKEN_LENGTH_KEY));
  return stored >= 1 ? stored : DEFAULT_MIN_TOKEN_LENGTH;
};

const SEARCH_ERROR = "Hubo un error en la búsqueda de clientes.";

const emptySearchResponse = (
  over: Partial<AccountSearchResponse> = {}
): AccountSearchResponse => ({
  model: [],
  didError: false,
  message: "",
  errorMessage: "",
  status: "Ok",
  hint: "None",
  reason: "None",
  hasMore: false,
  page: 1,
  pageSize: 20,
  containsEnabled: false,
  minTokenLength: getMinTokenLength(),
  source: "v2",
  ...over,
});

/**
 * El API manda el estado en cabecera tanto en legacy como en v2. Se acepta
 * cualquiera de las dos grafías (`BelowMinimum` o `below-minimum`) para no
 * depender del orden en que se desplieguen las patas.
 */
const parseSearchStatusHeader = (
  value: unknown
): AccountSearchStatus | undefined => {
  const flat = String(value ?? "")
    .replace(/[-_\s]/g, "")
    .toLowerCase();

  if (flat === "ok") return "Ok";
  if (flat === "belowminimum") return "BelowMinimum";
  if (flat === "unavailable") return "Unavailable";
  if (flat === "containsdisabled") return "ContainsDisabled";
  if (flat === "timedout") return "TimedOut";
  return undefined;
};

const parseSearchHintHeader = (value: unknown): AccountSearchHint => {
  const flat = String(value ?? "").toLowerCase();
  if (flat === "email") return "Email";
  if (flat === "phone") return "Phone";
  return "None";
};

/**
 * Endpoint legacy. Devuelve tres formas distintas y hay que tolerarlas todas:
 * un arreglo pelado en 200, un `ListResponse` en 504, y un arreglo vacío con
 * cabecera cuando el texto quedó por debajo del mínimo.
 */
export const getFilteredAccounts = async (
  criteria: string,
  options: { signal?: AbortSignal; maxRows?: number } = {}
): Promise<AccountSearchResponse> => {
  const { signal, maxRows = 20 } = options;
  const uri = `${baseURLAPI}/Account?idStr=${encodeURIComponent(
    criteria
  )}&maxRows=${maxRows}&withAgreementAuths=true`;

  try {
    const response = await api.get(uri, {
      signal,
      timeout: FILTERED_ACCOUNTS_TIMEOUT,
    });

    const headerStatus = parseSearchStatusHeader(
      response.headers?.["x-search-status"]
    );
    const hint = parseSearchHintHeader(response.headers?.["x-search-hint"]);

    // 200 con el arreglo pelado (o vacío, si el API cortó por mínimo).
    if (response.status === 200 && Array.isArray(response.data)) {
      return emptySearchResponse({
        model: response.data,
        status: headerStatus ?? "Ok",
        hint,
        source: "legacy",
        pageSize: maxRows,
      });
    }

    // 200 con `ListResponse`: el cuerpo manda sobre la cabecera.
    if (response.status === 200 && !!response.data) {
      const body = response.data as ResponseInterface<AccountInterface[]>;
      return emptySearchResponse({
        model: body.model ?? [],
        didError: !!body.didError,
        message: body.message ?? "",
        errorMessage: body.errorMessage ?? "",
        status: body.didError ? "TimedOut" : headerStatus ?? "Ok",
        hint,
        source: "legacy",
        pageSize: maxRows,
      });
    }
  } catch (error) {
    if (axios.isCancel(error)) {
      throw error;
    }

    const response = (error as any)?.response;
    const body = response?.data as
      | ResponseInterface<AccountInterface[]>
      | undefined;

    return emptySearchResponse({
      model: [],
      didError: true,
      message: body?.message ?? `Error: ${error}`,
      errorMessage: body?.errorMessage || SEARCH_ERROR,
      status: response?.status === 504 ? "TimedOut" : "Ok",
      hint: parseSearchHintHeader(response?.headers?.["x-search-hint"]),
      source: "legacy",
      pageSize: maxRows,
    });
  }

  return emptySearchResponse({
    didError: true,
    message: SEARCH_ERROR,
    errorMessage: SEARCH_ERROR,
    source: "legacy",
    pageSize: maxRows,
  });
};

/**
 * `POST /v2/Account/Search`. Siempre resuelve con un `AccountSearchResponse`:
 * el único caso que relanza es la cancelación, para que el hook la distinga de
 * un fallo de verdad.
 */
export const searchAccountsV2 = async (
  request: AccountSearchRequest,
  signal?: AbortSignal
): Promise<AccountSearchResponse> => {
  const uri = `${baseURLAPI}/v2/Account/Search`;

  try {
    const response = await api.post(uri, JSON.stringify(request), {
      headers: { "Content-Type": "application/json" },
      signal,
    });

    const data = (response.data ?? {}) as Partial<AccountSearchResponse>;
    rememberMinTokenLength(data.minTokenLength);

    return emptySearchResponse({
      model: data.model ?? [],
      didError: !!data.didError,
      message: data.message ?? "",
      errorMessage: data.errorMessage ?? "",
      status: data.status ?? parseSearchStatusHeader(
        response.headers?.["x-search-status"]
      ) ?? "Ok",
      hint: data.hint ?? parseSearchHintHeader(response.headers?.["x-search-hint"]),
      reason: data.reason ?? "None",
      hasMore: !!data.hasMore,
      page: data.page ?? request.page ?? 1,
      pageSize: data.pageSize ?? request.pageSize ?? 20,
      containsEnabled: !!data.containsEnabled,
      minTokenLength: data.minTokenLength ?? getMinTokenLength(),
      source: "v2",
    });
  } catch (error) {
    if (axios.isCancel(error)) {
      throw error;
    }

    const response = (error as any)?.response;
    const httpStatus: number | undefined = response?.status;
    const body = response?.data as
      | ResponseInterface<AccountInterface[]>
      | undefined;

    // El endpoint todavía no está desplegado: al legacy durante cinco minutos.
    if (httpStatus === 404) {
      markAccountSearchV2Unavailable();
      return emptySearchResponse({ status: "Unavailable" });
    }

    if (httpStatus === 504) {
      return emptySearchResponse({
        didError: true,
        status: "TimedOut",
        message: body?.message ?? "",
        errorMessage:
          body?.errorMessage || "La búsqueda tardó demasiado. Intenta de nuevo.",
      });
    }

    // Sin permiso: el endpoint existe, así que no se marca como no disponible.
    if (httpStatus === 403) {
      return emptySearchResponse({
        didError: true,
        message: body?.message ?? "",
        errorMessage:
          body?.errorMessage || "No tienes permiso para buscar clientes.",
      });
    }

    // Demasiadas consultas: el hook conserva lo que ya mostraba y espera.
    if (httpStatus === 429) {
      return emptySearchResponse({
        didError: true,
        rateLimited: true,
        message: body?.message ?? "",
        errorMessage: body?.errorMessage || SEARCH_ERROR,
      });
    }

    return emptySearchResponse({
      didError: true,
      message: body?.message ?? `Error: ${error}`,
      errorMessage: body?.errorMessage || SEARCH_ERROR,
    });
  }
};

/**
 * Lo que usa la barra en vivo. Prefiere v2 y cae al legacy cuando el 404 está
 * memorizado, mandándole el texto ya normalizado.
 */
export const searchAccountsLive = async (
  normalized: NormalizedSearch,
  options: { pageSize?: number; withAgreementAuths?: boolean } = {},
  signal?: AbortSignal
): Promise<AccountSearchResponse> => {
  const { pageSize = 20, withAgreementAuths = true } = options;

  if (normalized.route === "BelowMinimum") {
    return emptySearchResponse({
      status: "BelowMinimum",
      hint: normalized.hint,
      reason: normalized.reason,
      pageSize,
    });
  }

  if (!isAccountSearchV2Unavailable()) {
    const response = await searchAccountsV2(
      {
        mode: "Live",
        text: normalized.raw,
        withAgreementAuths,
        pageSize,
      },
      signal
    );

    // Sólo se cae al legacy cuando el 404 acaba de marcarse.
    if (!(response.status === "Unavailable" && isAccountSearchV2Unavailable())) {
      return response;
    }
  }

  return getFilteredAccounts(legacyQueryText(normalized), {
    signal,
    maxRows: pageSize,
  });
};

let v2Probe: { available: boolean; at: number } | null = null;
let v2ProbeInFlight: Promise<boolean> | null = null;

/**
 * Una sola consulta por sesión para saber si la búsqueda avanzada existe.
 * El cuerpo va vacío a propósito: el API responde `BelowMinimum` sin tocar la
 * base de datos, y de paso nos dice el `minTokenLength` vigente.
 */
export const probeAccountSearchV2 = (): Promise<boolean> => {
  if (isAccountSearchV2Unavailable()) {
    return Promise.resolve(false);
  }

  if (
    v2Probe &&
    (v2Probe.available || Date.now() - v2Probe.at < ACCOUNT_SEARCH_V2_RETRY_MS)
  ) {
    return Promise.resolve(v2Probe.available);
  }

  if (v2ProbeInFlight) {
    return v2ProbeInFlight;
  }

  v2ProbeInFlight = searchAccountsV2({ mode: "Live", text: "" })
    .then((response) => {
      const available = response.status !== "Unavailable";
      v2Probe = { available, at: Date.now() };
      return available;
    })
    .catch(() => false)
    .finally(() => {
      v2ProbeInFlight = null;
    });

  return v2ProbeInFlight;
};

/** Sólo para las pruebas: olvida el sondeo de esta sesión. */
export const resetAccountSearchV2Probe = (): void => {
  v2Probe = null;
  v2ProbeInFlight = null;
};

/**
 * Sales window we ask for. The procedure's own default is already 30
 * (`@LastSalesDays INT = 30`, verified 2026-09-12); it is the .NET layer that
 * defaults to 90, so sending this explicitly is what keeps the window narrow.
 */
export const DEFAULT_LAST_SALES_DAYS = 30;

/**
 * The only two values the API accepts. Anything else is a 400 with
 * "LastSalesDays debe ser 30 o 90" (measured against dev 2026-09-13), so the
 * type keeps a caller from discovering that at runtime.
 */
export type LastSalesDays = 30 | 90;

const FAVORITE_DESTINATIONS_TTL = 5 * 60 * 1000;
const FAVORITE_DESTINATIONS_TIMEOUT = 15000;

interface FavoriteDestinationsCacheEntry {
  expiresAt: number;
  promise: Promise<ResponseInterface<RecurrentDestinationInterface[]>>;
}

/**
 * The consignee book, keyed by shipper and held in memory only.
 *
 * No Redux and no storage: redux-persist wraps the whole root reducer with no
 * blacklist (store.tsx), so a slice would write consignee PII to localStorage.
 * A module-level Map dies with the page and is cleared at logout.
 */
const favoriteDestinationsCache = new Map<
  string,
  FavoriteDestinationsCacheEntry
>();

const favoriteDestinationsError =
  (): ResponseInterface<RecurrentDestinationInterface[]> => ({
    model: null,
    didError: true,
    message: "Hubo un error obteniendo el historial de búsquedas.",
    errorMessage: "Hubo un error obteniendo el historial de búsquedas.",
  });

/**
 * A rejection only carries a usable payload when the server answered with our
 * own envelope. A network error, a timeout (ECONNABORTED) or a failed token
 * refresh reject with something else entirely, and returning that raw value
 * would make every caller throw on `response.didError`.
 */
const isResponseShape = (
  data: any
): data is ResponseInterface<RecurrentDestinationInterface[]> =>
  !!data && typeof data === "object" && typeof data.didError === "boolean";

/**
 * Drop cached books. With no argument it clears every shipper — call it at
 * logout, since tellers share a PC and the book is customer PII.
 */
export const invalidateFavoriteDestinationsCache = (shipperId?: string) => {
  if (shipperId === undefined) {
    favoriteDestinationsCache.clear();
    return;
  }

  favoriteDestinationsCache.delete(shipperId);
};

const requestFavoriteAndHistoricalLocations = async (
  accountId: string,
  sanitizedSearch: string,
  lastSalesDays: LastSalesDays
): Promise<ResponseInterface<RecurrentDestinationInterface[]>> => {
  const uri =
    `${baseURLAPI}/GetFavoriteAndRecentDestinations` +
    `?AccountID=${encodeURIComponent(accountId)}` +
    `&SearchInput=${encodeURIComponent(sanitizedSearch)}` +
    `&LastSalesDays=${lastSalesDays}`;

  try {
    // The stored procedure is what blocks production, so bound the wait: with
    // no timeout a hung request leaves the dropdown spinning indefinitely.
    const response = await api.get(uri, {
      timeout: FAVORITE_DESTINATIONS_TIMEOUT,
    });

    if (
      response.status === 200 &&
      !response.data.didError &&
      !!response.data.model
    ) {
      return {
        message: "",
        didError: false,
        errorMessage: "",
        model: response.data.model,
      };
    } else if (response.status === 200 && isResponseShape(response.data)) {
      return response.data;
    }
  } catch (error) {
    const data = (error as any)?.response?.data;
    if (isResponseShape(data)) {
      return data;
    }
  }

  return favoriteDestinationsError();
};

export const getFavoriteAndHistoricalLocations = async (
  accountId: string,
  search: string = "",
  lastSalesDays: LastSalesDays = DEFAULT_LAST_SALES_DAYS
): Promise<ResponseInterface<RecurrentDestinationInterface[]>> => {
  const sanitizedSearch = sanitizeFulltextQuery(search);

  // Only the empty search — the shipper's whole book — is cached. A filtered
  // search is a different, uncapped query and is always issued.
  if (sanitizedSearch !== "" || lastSalesDays !== DEFAULT_LAST_SALES_DAYS) {
    return requestFavoriteAndHistoricalLocations(
      accountId,
      sanitizedSearch,
      lastSalesDays
    );
  }

  const now = Date.now();
  const cached = favoriteDestinationsCache.get(accountId);
  if (!!cached && cached.expiresAt > now) {
    return cached.promise;
  }

  // Sweep on insert: expiry is otherwise only checked on read, so a teller
  // moving through many shippers would keep every book resident all day.
  favoriteDestinationsCache.forEach((entry, key) => {
    if (entry.expiresAt <= now) {
      favoriteDestinationsCache.delete(key);
    }
  });

  const entry: FavoriteDestinationsCacheEntry = {
    expiresAt: now + FAVORITE_DESTINATIONS_TTL,
    // The stored promise must never reject: a lote mounts one search box per
    // guía and they all await this same promise.
    promise: requestFavoriteAndHistoricalLocations(
      accountId,
      sanitizedSearch,
      lastSalesDays
    )
      .then((response) =>
        !!response && !response.didError && !!response.model
          ? response
          : favoriteDestinationsError()
      )
      .catch(() => favoriteDestinationsError()),
  };

  // Stored synchronously, before the first await, so every box mounted in the
  // same commit shares one request instead of firing one each.
  favoriteDestinationsCache.set(accountId, entry);

  entry.promise.then((response) => {
    // Compare identity before evicting: an invalidation plus a fresh fetch can
    // happen while this request is still in flight, and its late failure must
    // not throw away the newer entry.
    if (
      response.didError &&
      favoriteDestinationsCache.get(accountId) === entry
    ) {
      favoriteDestinationsCache.delete(accountId);
    }
  });

  return entry.promise;
};

export const getAccountWhitelist = async (
  taxIdentificationTypes: TaxIdentificationTypeInterface[],
  accountId?: string,
  agreementID?: string,
  isLocked?: boolean
): Promise<ResponseInterface<WhiteLocationInterface>> => {
  const params = new URLSearchParams();
  if (accountId) params.append("AccountID", accountId);
  if (agreementID) params.append("AgreementID", agreementID);
  if (isLocked) params.append("IsLocked", String(isLocked));
  const uri = `${baseURLAPI}/Agreement/Location?${params.toString()}`;

  try {
    const response = await api.get(uri);

    if (
      response.status === 200 &&
      !response.data.didError &&
      !!response.data.model
    ) {
      const whiteLocations = response.data.model;

      // Map the objet to a AgreementLocationsListInterface
      return {
        message: "",
        didError: false,
        errorMessage: "",
        model: {
          accountID: whiteLocations.accountID,
          consigneeDestinationList: whiteLocations.consigneeDestinationList.map(
            (destination: DestinationDto) =>
              mapToDestinationInterface(destination, taxIdentificationTypes)
          ),
        },
      };
    } else if (response.status === 200) {
      return response.data;
    }
  } catch (error) {
    return (error as any)?.response?.data;
  }

  return {
    model: null,
    didError: true,
    message: "Hubo un error obteniendo las cuentas permitidas.",
    errorMessage: "Hubo un error obteniendo las cuentas permitidas.",
  };
};

// Method to map DestinationDto to DestinationInterface
export const mapToDestinationInterface = (
  destination: DestinationDto,
  taxIdTypes: TaxIdentificationTypeInterface[]
): DestinationInterface => {
  const taxIdType = taxIdTypes.find(
    (taxIdType) =>
      taxIdType.taxIdentificationTypeId === destination.taxIdentificationTypeID
  );
  return {
    client: {
      id: destination.accountID,
      accountCode: destination.accountCode,
      taxIdentificationTypeID: destination.taxIdentificationTypeID,
      identificationNumber: destination.identificationNumber,
      accountFullName: destination.accountFullName,
      taxIdentificationTypeCode: taxIdType?.taxIdentificationTypeCode ?? "",
      abreviationName: taxIdType?.abreviationName ?? "",
      listAccountPhone: destination.accountPhoneList.map((phone) => ({
        phoneID: phone.accountPhoneID,
        accountID: destination.accountID,
        countryID: phone.accountPhoneCountryID,
        phoneNumber: phone.accountPhoneNumber,
        countryPhoneAccessCode: phone.accountCountryPhoneAccessCode,
        phoneTypeID: phone.accountPhoneTypeID,
      })),
      listAccountEmail: [],
      listAuthorizingAccount: [],
      creationDate: "",
      creationUser: "",
    },
    location: {
      name: destination.addressName,
      address:
        destination.addressLine1 +
        (destination.addressLine2 !== null
          ? " " + destination.addressLine2
          : ""),
      reference: destination.addressLandMark,
      postalCode: destination.postalCode,
      coordinates: {
        lat: destination.latitude ?? 0,
        lng: destination.longitude ?? 0,
      },
    },
    businessUnit: {
      id: destination.businessUnit.buid.toString(),
      name: destination.businessUnit.buName,
      code: destination.businessUnit.buCode,
      location: {
        name: destination.businessUnit.buAreaName ?? "",
        address: destination.businessUnit.address ?? "",
        reference: destination.businessUnit.reference ?? "",
        postalCode: destination.businessUnit.postalCode ?? "",
        coordinates: {
          lat: !!destination.businessUnit.latitude
            ? parseFloat(destination.businessUnit.latitude)
            : 0,
          lng: !!destination.businessUnit.longitude
            ? parseFloat(destination.businessUnit.longitude)
            : 0,
        },
      },
      polygonList: [],
    },
    homeDelivery: destination.isHomeDelivery,
  };
};

export const verifyAccountBlocked = async (
  accountID: string,
  serviceID: number,
  paymentModeID: number,
  buCode: string
): Promise<ResponseInterface<AccountBlocked[]>> => {
  const CancelToken = axios.CancelToken;
  const source = CancelToken.source();
  const uri = `${baseURLAPI}/Account/VerifyAccountBlocked?AccountID=${accountID}&ServiceID=${serviceID}&PaymentModeID=${paymentModeID}&BUCode=${buCode}`;

  try {
    const response = await api.get(uri, {
      headers: {
        "Content-Type": "application/json",
      },
      cancelToken: source.token,
    });

    if (response.status === 200) {
      return response.data;
    }
    return {
      model: null,
      didError: true,
      message: "",
      errorMessage: "Hubo un error al verificar la lista negra de los cupones",
    };
  } catch (error) {
    return (error as any)?.response?.data;
  }
};

export const reserveBUSeller = async (
  buSeller: string,
  accountID: string,
  user: string
): Promise<ResponseInterface<BUSellerConfig>> => {
  const CancelToken = axios.CancelToken;
  const source = CancelToken.source();
  const uri = `${baseURLAPI}/Account/ReserveBUSeller`;

  const body = JSON.stringify({
    BUSeller: buSeller,
    AccountID: accountID,
    User: user,
  });

  try {
    const response = await api.post(uri, body, {
      headers: {
        "Content-Type": "application/json",
      },
      cancelToken: source.token,
    });

    return response.data;
  } catch (error) {}

  return {
    model: null,
    didError: true,
    message: "",
    errorMessage: "",
  };
};

export const getAccountSiteBUByAccountSiteID = async (
  accountSiteID: string
): Promise<ResponseInterface<AccountSiteBUDTO[]>> => {
  const CancelToken = axios.CancelToken;
  const source = CancelToken.source();
  const uri = `${baseURLAPI}/Account/GetAccountSiteBUByAccountSiteID?AccountSiteID=${accountSiteID}`;

  try {
    const response = await api.get(uri, {
      headers: {
        "Content-Type": "application/json",
      },
      cancelToken: source.token,
    });
    
    return response.data;
  } catch (error) {}
    return {
      model: null,
      didError: true,
      message: "",
      errorMessage: "",
    };
}

export const getAccountSiteLockByAccountSiteID = async (
  accountSiteID: string
): Promise<ResponseInterface<AccountSiteLockDTO[]>> => {
  const CancelToken = axios.CancelToken;
  const source = CancelToken.source();
  const uri = `${baseURLAPI}/Account/GetAccountSiteLockByAccountSiteID?AccountSiteID=${accountSiteID}`;

  try {
    const response = await api.get(uri, {
      headers: {
        "Content-Type": "application/json",
      },
      cancelToken: source.token,
    });
    
    return response.data;
  } catch (error) {}
    return {
      model: null,
      didError: true,
      message: "",
      errorMessage: "",
    };
}

